import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { UsernatorPromiseClient } from "../generated/user_grpc_web_pb";
import { LoginRequest, UserData as UserDataMessage } from "../generated/user_pb";
import GRPC_BACKEND_URL, { GRPC_CLIENT_OPTIONS, SSO_ENABLED } from "../grpcConfig";
import { hashStringSHA256, isNetworkError } from "@dowty/core";
import { outbox, reads, setOutboxUser } from "../offline";
import { removePushBeforeLogout } from "../services/pushService";
import { INTERACTIVE_SSO_KEY, SILENT_SSO_KEY, SSO_SESSION_KEY, startSignIn, submitGlobalSignOut } from "../auth/signIn";
import { readStorage, removeStorage, writeStorage } from "../utils/safeStorage";

export type AuthUser = {
    userId: number;
    username: string;
    token: string;
};

type AuthContextValue = {
    user: AuthUser | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (username: string, password: string) => Promise<void>;
    register: (username: string, password: string) => Promise<void>;
    logout: (discardPending: boolean) => Promise<void>;
    isSsoSession: boolean;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const STORAGE_KEY = "saufdings-auth";

const canAttemptSilentSso = (): boolean => SSO_ENABLED
    && readStorage(SILENT_SSO_KEY, true) === null
    && readStorage(INTERACTIVE_SSO_KEY, true) === null
    && !/^\/(?:impressum|datenschutz)\/?$/.test(window.location.pathname)
    && new URLSearchParams(window.location.search).get("sso") !== "none";

type StoredUser = { user: AuthUser; source: "cookie" | "localStorage" };

const readStoredUser = (): StoredUser | null => {
    if (typeof window === "undefined") return null;

    const cookieMatch = !SSO_ENABLED && document.cookie.split("; ").find((r) => r.startsWith("dowtex-auth="));
    if (cookieMatch) {
        try {
            const cookieUser = JSON.parse(
                decodeURIComponent(cookieMatch.split("=").slice(1).join("="))
            ) as AuthUser;
            if (cookieUser?.token) return { user: cookieUser, source: "cookie" };
        } catch {}
    }

    const raw = readStorage(STORAGE_KEY);
    if (!raw) return null;
    try {
        const user = JSON.parse(raw) as AuthUser;
        if (typeof user?.token !== "string" || !user.token) {
            removeStorage(STORAGE_KEY);
            return null;
        }
        return { user, source: "localStorage" };
    } catch {
        removeStorage(STORAGE_KEY);
        return null;
    }
};

const mapUser = (message: UserDataMessage): AuthUser => {
    const token = message.getToken();
    if (!token) {
        throw new Error("Token missing in login response");
    }

    return {
        userId: message.getUserid(),
        username: message.getUsername(),
        token,
    };
};

const buildUserMessage = (user: AuthUser): UserDataMessage => {
    const userMessage = new UserDataMessage();
    userMessage.setUserid(user.userId);
    userMessage.setUsername(user.username);
    userMessage.setToken(user.token);
    return userMessage;
};

const userClient = new UsernatorPromiseClient(GRPC_BACKEND_URL, null, GRPC_CLIENT_OPTIONS);

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const storedRef = useRef<StoredUser | null>(readStoredUser());
    const [user, setUser] = useState<AuthUser | null>(storedRef.current?.user ?? null);
    const [isLoading, setIsLoading] = useState<boolean>(() =>
        typeof window !== "undefined" && !storedRef.current?.user.token && canAttemptSilentSso()
    );
    const [isSsoSession, setIsSsoSession] = useState<boolean>(() =>
        SSO_ENABLED && typeof window !== "undefined" && readStorage(SSO_SESSION_KEY, true) === "true"
    );
    const hasValidatedInitialUser = useRef<boolean>(false);
    const invalidateSession = useCallback(() => {
        setUser(null);
        setIsSsoSession(false);
        setIsLoading(false);
        removeStorage(SSO_SESSION_KEY, true);
        setOutboxUser(null);
    }, []);

    useEffect(() => {
        const activeUserId = user?.userId;
        if (!SSO_ENABLED || activeUserId === undefined) return;
        let active = true;
        const validate = async () => {
            try {
                const response = await window.fetch(new URL("/sso/session", GRPC_BACKEND_URL).href, {
                    credentials: "include",
                    cache: "no-store",
                });
                if (active && response.status === 401) {
                    void reads.clear();
                    invalidateSession();
                }
            } catch {
                // A temporary network failure does not invalidate the locally visible session.
            }
        };
        const interval = window.setInterval(() => { void validate(); }, 60_000);
        window.addEventListener("focus", validate);
        return () => {
            active = false;
            window.clearInterval(interval);
            window.removeEventListener("focus", validate);
        };
    }, [invalidateSession, user?.userId]);

    useEffect(() => {
        const outboxUser = SSO_ENABLED && !hasValidatedInitialUser.current ? null : user;
        setOutboxUser(outboxUser);
        if (outboxUser) void outbox.flush();
        if (!user?.token) {
            removeStorage(STORAGE_KEY);
            if (user) removeStorage(INTERACTIVE_SSO_KEY, true);
            return;
        }
        if (!(storedRef.current?.source === "cookie" && user === storedRef.current.user)) {
            writeStorage(STORAGE_KEY, JSON.stringify(user));
        }
        removeStorage(INTERACTIVE_SSO_KEY, true);
    }, [user]);

    useEffect(() => {
        const stored = storedRef.current;
        if (hasValidatedInitialUser.current) return;

        hasValidatedInitialUser.current = true;
        const url = new URL(window.location.href);
        const gotNoSsoSession = url.searchParams.get("sso") === "none";
        if (gotNoSsoSession) {
            url.searchParams.delete("sso");
            window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
            if (readStorage(SILENT_SSO_KEY, true) !== "signed-out") {
                writeStorage(SILENT_SSO_KEY, "none", true);
            }
        }

        // An explicit sign-out only suppresses silent sign-in for the load that follows it; a later load (for example after signing in as someone else in the hub) tries again.
        const signedOutBeforeLoad = readStorage(SILENT_SSO_KEY, true) === "signed-out";
        const attemptSilentSso = () => {
            if (!gotNoSsoSession && canAttemptSilentSso()) {
                writeStorage(SILENT_SSO_KEY, "tried", true);
                setIsLoading(true);
                startSignIn(true);
                return true;
            } else {
                if (signedOutBeforeLoad) removeStorage(SILENT_SSO_KEY, true);
                setIsLoading(false);
                return false;
            }
        };

        const validateExistingSession = async () => {
            if (!stored || stored.source === "cookie" || !stored.user.token) {
                storedRef.current = null;
                setIsLoading(false);
                return;
            }

            let startingSilentSso = false;
            try {
                const response = await userClient.validateSession(buildUserMessage(stored.user), {});
                const refreshed = mapUser(response);
                setUser(refreshed);
            } catch (error) {
                // A network-shaped failure (no connection yet, backend unreachable) is not proof
                // the token is bad - it just means this device hasn't been able to ask. Logging
                // the user out here would fail the offline case at the door: AddDrink.tsx gates
                // its whole form on isAuthenticated, so the drink/activity read caches under
                // src/offline/ never get a chance to render if this clears `user` before that
                // gate is even reached on a fresh, offline app load. Only a genuine rejection
                // (token actually invalid, expired, etc.) should log the stored session out; keep
                // it otherwise and let the next successful validateSession (or an outbox flush,
                // which carries its own captured token) reconcile things once connectivity
                // returns.
                if (isNetworkError(error)) {
                    console.warn("Session validation failed (offline?) - keeping stored user", error);
                    if (SSO_ENABLED) {
                        setOutboxUser(stored.user);
                        void outbox.flush();
                    }
                } else {
                    console.warn("Existing session validation rejected, clearing user", error);
                    setUser(null);
                    startingSilentSso = attemptSilentSso();
                }
            } finally {
                storedRef.current = null;
                if (!startingSilentSso && !stored?.user.token) setIsLoading(false);
            }
        };

        if (SSO_ENABLED) {
            const restoreSsoSession = async () => {
                try {
                    const response = await window.fetch(new URL("/sso/session", GRPC_BACKEND_URL).href, {
                        credentials: "include",
                        cache: "no-store",
                    });
                    if (response.status === 401) {
                        removeStorage(SSO_SESSION_KEY, true);
                        setIsSsoSession(false);
                        if (stored?.user.token) {
                            await validateExistingSession();
                        } else {
                            setUser(null);
                            attemptSilentSso();
                        }
                        return;
                    }
                    if (response.status !== 200) {
                        await validateExistingSession();
                        return;
                    }

                    const identity = await response.json();
                    if (typeof identity?.userId !== "number" || typeof identity?.username !== "string") {
                        await validateExistingSession();
                        return;
                    }
                    setUser({ userId: identity.userId, username: identity.username, token: "" });
                    setIsSsoSession(true);
                    removeStorage(SILENT_SSO_KEY, true);
                    setIsLoading(false);
                } catch {
                    await validateExistingSession();
                }
            };

            void restoreSsoSession();
        } else {
            void validateExistingSession();
        }
    }, []);

    const login = useCallback(
        async (username: string, password: string) => {
            setIsLoading(true);
            try {
                const request = new LoginRequest();
                request.setUsername(username);
                const hashedPassword = await hashStringSHA256(password);
                request.setPassword(hashedPassword);
                const response = await userClient.login(request, {});
                const authenticated = mapUser(response);
                setUser(authenticated);
            } finally {
                setIsLoading(false);
            }
        },
        []
    );

    const register = useCallback(async (username: string, password: string) => {
        setIsLoading(true);
        try {
            const request = new LoginRequest();
            request.setUsername(username);
            const hashedPassword = await hashStringSHA256(password);
            request.setPassword(hashedPassword);
            await userClient.register(request, {});
        } finally {
            setIsLoading(false);
        }
    }, []);

    const logout = useCallback(async (discardPending: boolean) => {
        if (user) await removePushBeforeLogout(user);
        if (isSsoSession) {
            const response = await window.fetch(new URL("/sso/app-logout", GRPC_BACKEND_URL).href, {
                method: "POST",
                credentials: "include",
            });
            if (!response.ok) throw new Error("SSO logout failed");
            removeStorage(SSO_SESSION_KEY, true);
            setIsSsoSession(false);
        }
        await Promise.all([reads.clear(), ...(discardPending ? [outbox.clear()] : [])]);
        setUser(null);
        invalidateSession();
        if (SSO_ENABLED) {
            writeStorage(SILENT_SSO_KEY, "signed-out", true);
            submitGlobalSignOut();
        }
    }, [invalidateSession, isSsoSession, user]);

    const value = useMemo<AuthContextValue>(
        () => ({
            user,
            isAuthenticated: Boolean(user),
            isLoading,
            login,
            register,
            logout,
            isSsoSession,
        }),
        [isLoading, isSsoSession, login, logout, register, user]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
